/* eslint-disable @typescript-eslint/no-explicit-any */
import { FetchResult } from '@apollo/client'
import React, { Children, cloneElement, Component, isValidElement } from 'react'
import { Dimmer, Form as SUIForm, FormProps as SUIFormProps, Label, Loader, MessageProps } from 'semantic-ui-react'
import { errorsFrom, FlashMessage, GlobalFlashMessage, messageForErrorSymbol, t } from 'shared'
export type SuccessHandler<U extends Record<string, any>> = (data: SuccessData<U>) => void
export type FormProps<V, U extends Record<string, any>> = {
  beforeSubmit?: (values: V) => Promise<V>
  defaults?: Partial<V> | (() => Partial<V>)
  extraValues?: Partial<V> | (() => Partial<V>)
  initialValues?: Partial<V>
  loading?: boolean
  mutation: (options: { variables: V }) => Promise<FetchResult<U>>
  onChange?: (...args: any[]) => void
  onError?: (...args: any[]) => void
  onSuccess?: SuccessHandler<U>
  successMessage?: string | React.ReactElement
  visible?: boolean
  extractGlobalErrorMessage?: (errors: Record<string, string>) => string | undefined
} & SUIFormProps
export type SuccessData<U, K extends keyof U = keyof U> = Omit<U[K], 'messages'>
type FormState<V extends {}, U extends Record<string, any>> = {
  error?: boolean
  values: V
  errors: { [key in keyof V]?: string } & { base?: string }
  dirty?: boolean
  successData: SuccessData<U>
  submitting?: boolean
  success?: boolean
  mounted: boolean
}

export type InputFormProp<V extends {}, U extends Record<string, any>> = {
  defaults: Partial<V>
  dirty: boolean | undefined
  errors: { [key in keyof V]?: string | undefined } & {
    base?: string | undefined
  }
  submit: () => void
  submitting: boolean | undefined
  submitPromise: () => Promise<unknown>
  successData: SuccessData<U>
  updateValues: (newValues: Partial<V>) => void
  values: V
}

const undefinedToNull = <T extends Record<string, any>>(vars?: T) => {
  const v = (vars ?? {}) as Record<string, any>
  const ret = Object.keys(v).reduce(
    (agg, key) => {
      agg[key] = typeof v[key] === 'undefined' ? null : v[key]
      return agg
    },
    {} as Record<string, any>
  )
  return ret as T
}

export class Form<V extends RequiredKeys<V>, U extends Record<string, any>> extends Component<
  FormProps<V, U>,
  FormState<V, U>
> {
  componentDidMount() {
    this.setState({ mounted: true })
  }
  componentWillUnmount() {
    this.setState({ mounted: false })
  }
  constructor(props: FormProps<V, U>) {
    super(props)
    this.state = {
      mounted: false,
      errors: {},
      successData: {} as SuccessData<U>,
      values: (props.initialValues as V) || (this.unwrap(props.defaults) as V) || ({} as V),
    }
    props.onChange && props.onChange({}, this.state.values, this.updateValues)
  }

  get defaults() {
    return this.unwrap(this.props.defaults)
  }

  get extraValues() {
    return this.unwrap(this.props.extraValues)
  }

  unwrap = <T extends (() => Partial<V>) | Partial<V> | undefined>(objOrFn: T): Partial<V> => {
    const vars: Record<string, any> =
      (objOrFn && (typeof objOrFn === 'function' ? objOrFn() : (objOrFn as Partial<V>))) || ({} as Partial<V>)

    return vars as Partial<V>
  }

  updateValues = (next: Partial<V>) => {
    const values = Object.assign({}, this.state.values, next)
    this.props.onChange && this.props.onChange(this.state.values, values, this.updateValues)
    this.setState({ dirty: true, values, error: false })
  }

  // Submits and catches any rejection of the `mutatePromise`.
  // Only the default error handling and display will apply.
  submit = () => {
    if (this.state.submitting) return
    this.submitPromise().catch((err) => console.warn(err))
  }

  // Trigger this directly and catch() to inject special error handling.
  // The default error handling and display will also apply.
  submitPromise = () => {
    this.setState({ error: false, submitting: true, success: false })
    const vars = undefinedToNull<V>(Object.assign({}, this.defaults, this.state.values, this.extraValues))
    // console.log(this.defaults, vars)

    const { beforeSubmit } = this.props

    if (beforeSubmit) {
      return beforeSubmit(vars)
        .catch((err) => {
          this.setState({ submitting: false })
        })
        .then((updatedVars) => updatedVars && this.mutatePromise(updatedVars))
    }
    return this.mutatePromise(vars)
  }

  mutatePromise = (variables: V) => {
    const { mutation, onError, onSuccess } = this.props
    return new Promise((resolve, reject) => {
      mutation({ variables }).then(({ data }) => {
        // assuming that the error/success data is always wrapped
        if (!data) {
          // console.log('no data -> onError', this.state.mounted)
          onError && onError({})
          if (!this.state.mounted) return
          this.setState(
            { error: true, errors: {}, submitting: false, success: false, successData: {} as SuccessData<U> },
            () => {
              reject({})
            }
          )
          return
        }
        const { messages, ...successData } = data[Object.keys(data)[0]]

        if (messages) {
          const errors = errorsFrom({ messages }) as FormState<V, U>['errors']
          onError && onError(errors)
          // console.log(' onError', this.state.mounted)

          if (!this.state.mounted) return

          this.setState(
            { error: true, errors, submitting: false, success: false, successData: {} as SuccessData<U> },
            () => {
              reject(errors)
            }
          )
          return
        }

        onSuccess?.({ variables, ...successData })

        if (!this.state.mounted) return

        this.setState({ dirty: false, error: false, errors: {}, submitting: false, success: true, successData }, () => {
          resolve(successData)
        })
      })
    })
  }

  render() {
    const {
      beforeSubmit,
      children,
      defaults,
      extraValues,
      initialValues,
      loading,
      mutation,
      onChange,
      onError,
      onSuccess,
      successMessage,
      visible,
      extractGlobalErrorMessage,
      ...suiFormProps
    } = this.props

    if (visible === false) return null

    const globalError =
      (this.state.error && extractGlobalErrorMessage && extractGlobalErrorMessage(this.state.errors)) || undefined
    return (
      <SUIForm onSubmit={this.submit} error={this.state.error} {...suiFormProps}>
        <Dimmer active={loading === undefined ? this.state.submitting : loading} inverted>
          <Loader active />
        </Dimmer>
        {this.state.error && <FormError message={globalError} />}
        {this.state.success && !this.props.onSuccess && <FormSuccess message={successMessage} />}
        {deepMap(children, (child) => {
          if (child.props && child.props.as === 'button') return child
          return cloneElement(child, {
            form: {
              defaults: this.defaults,
              dirty: this.state.dirty,
              errors: this.state.errors,
              submit: this.submit,
              submitting: this.state.submitting,
              submitPromise: this.submitPromise,
              successData: this.state.successData,
              updateValues: this.updateValues,
              values: this.state.values,
            } as InputFormProp<V, U>,
          })
        })}
      </SUIForm>
    )
  }
}

// this goes through the tree marked up within <Form>, but no sub render() funcs
const deepMap = (children: React.ReactNode, func: (child: React.ReactElement) => React.ReactElement): React.ReactNode =>
  Children.map(children, (child) => {
    if (!isValidElement(child)) return child
    if (child.props.children)
      return cloneElement(func(child), {
        children: deepMap(child.props.children, func),
      })
    return func(child)
  })

export const FormError = ({ message = t('error.check_input') }: { message?: MessageProps['content'] }) => (
  <FlashMessage error content={message} />
)

export const FormSuccess = ({ message = t('forms.changes_saved') }: { message?: MessageProps['content'] }) => (
  <GlobalFlashMessage success content={message} />
)

export const FormInputError = ({ message, errorSymbol }: { message?: string; errorSymbol?: string }) => {
  if (!message && !errorSymbol) return null

  return (
    <Label basic color="red" pointing="above" size="small">
      {message || (errorSymbol && messageForErrorSymbol(errorSymbol))}
    </Label>
  )
}

export const FormBaseError = <V extends {}, U extends { data?: any }>({
  form: {
    errors: { base },
  },
}: {
  form: InputFormProp<V, U>
}) => <FormInputError message={base && t(`error.${base}`)} />
